import { z } from 'zod'

import type { CountryCode } from '@/types/shopify/admin/api-types'

export type FORMIO_FIELDS = {
  hotWater: boolean
  cooking: boolean
  heating: boolean
  other: boolean
  email: string
  otherInput?: string
}

const AU_PHONE_REGEX = /^(\+?61\s?[23478]\s?\d{8}|\(?0[23478]\)?\s?\d{8})$/
const PHONE_NUMBER_SCHEMA = z.string().trim().regex(AU_PHONE_REGEX, 'Invalid Australian phone number')

// Names must contain printable ASCII characters only. Pronto accepts the full ASCII
// range, so standard punctuation (e.g. & ' " -) is allowed, but accented and other
// non-ASCII characters are rejected to avoid corrupted values in the Pronto payload.
const ASCII_ONLY_REGEX = /^[\x20-\x7E]*$/
const ASCII_ONLY_MESSAGE = 'Please use standard English characters only (no accented or non-English characters)'

const baseSchema = z.object({
  firstName: z.string().min(1, 'First name is required').regex(ASCII_ONLY_REGEX, ASCII_ONLY_MESSAGE),
  lastName: z.string().min(1, 'Last name is required').regex(ASCII_ONLY_REGEX, ASCII_ONLY_MESSAGE),
  phone: PHONE_NUMBER_SCHEMA,
  address: z.object({
    address1: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    countryCode: z.custom<CountryCode>().nullable().optional(),
    zip: z.string().nullable().optional(),
    zoneCode: z.string().nullable().optional(),
  }),
})

const personalSchema = baseSchema.extend({
  isBusiness: z.literal(false),
})

const businessSchema = baseSchema.extend({
  isBusiness: z.literal(true),
  businessName: z.string().min(1, 'Business name is required').regex(ASCII_ONLY_REGEX, ASCII_ONLY_MESSAGE),
  industry: z.string().min(1, 'Industry is required'),
  ABN: z.string().length(11, 'ABN must be 11 digits'),
})

export const formSchema = z.discriminatedUnion('isBusiness', [personalSchema, businessSchema])

export type OnBoardingFormDataType = z.infer<typeof formSchema>

type FormData = OnBoardingFormDataType

export type FormErrors = Partial<Record<keyof (Extract<FormData, { isBusiness: false }> & Extract<FormData, { isBusiness: true }>), string | string[]>>

export type InputError = { message: string }

export type UserError = { field?: Array<keyof FormErrors>; message: string }

export type MutationResult = { error?: { inputErrors?: InputError[]; userErrors?: UserError[] } }
